import { useMemo, useState } from 'react';
import {
  formatCurrency,
  formatMaybeCurrency,
  formatPercent,
} from '@shared/SharedAccountBody';
import { RetailAccountContent } from '@shared/types';
import { computePositionTotals, filterOpenPositions } from './portfolioUtils';
import { DataCard } from '@/components/nq/DataCard';
import { PLBadge } from '@/components/nq/PLBadge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

type SortKey =
  | 'symbol'
  | 'weight'
  | 'avgEntryPrice'
  | 'currentPrice'
  | 'marketValue'
  | 'unrealizedPL'
  | 'unrealizedPLPct';

type SortDir = 'asc' | 'desc';

interface PositionRow {
  symbol: string;
  weightPct: number;
  qty?: number;
  avgEntryPrice?: number;
  currentPrice?: number;
  marketValue?: number;
  unrealizedPL?: number;
  unrealizedPLPct?: number;
  /** Earliest / latest buy fill for this open position (ISO-8601). */
  heldSince?: string;
  /** Most recent sell fill for this symbol (ISO-8601). */
  lastSoldAt?: string;
}

export type { PositionRow };

const MUTED_COLUMNS = new Set<SortKey>(['avgEntryPrice', 'currentPrice']);

const headClassName = (key: SortKey, sorted: boolean) =>
  [
    'cursor-pointer select-none whitespace-nowrap hover:text-foreground',
    key === 'symbol' ? 'text-left' : 'text-right',
    key === 'unrealizedPLPct' ? 'text-center' : '',
    sorted ? (MUTED_COLUMNS.has(key) ? 'text-primary/85' : 'text-primary') : '',
  ]
    .filter(Boolean)
    .join(' ');

const cellClassName = (key: SortKey, extra?: string) =>
  [
    'whitespace-nowrap py-3 tabular-nums',
    key === 'symbol' ? 'text-left' : 'text-right',
    key === 'unrealizedPLPct' ? 'text-center' : '',
    MUTED_COLUMNS.has(key) ? 'text-muted-foreground' : '',
    extra,
  ]
    .filter(Boolean)
    .join(' ');

const isPlUp = (pl?: number, plPct?: number): boolean | undefined => {
  if (plPct !== undefined) return plPct >= 0;
  if (pl !== undefined) return pl >= 0;
  return undefined;
};

interface PortfolioPositionsTableProps {
  content?: RetailAccountContent;
  loading?: boolean;
  error?: string | null;
  isUnfunded?: boolean;
  selectedSymbol?: string | null;
  onPositionSelect?: (row: PositionRow) => void;
}

const PortfolioPositionsTable = ({
  content,
  loading,
  error,
  isUnfunded = false,
  selectedSymbol,
  onPositionSelect,
}: PortfolioPositionsTableProps) => {
  const [sortKey, setSortKey] = useState<SortKey>('weight');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const { rows } = useMemo(() => {
    const positions = filterOpenPositions(content?.positions ?? []);
    const invested =
      content?.stockValue ??
      positions.reduce((sum, p) => sum + (p.marketValue ?? 0), 0);

    const mapped: PositionRow[] = positions.map((p) => ({
      symbol: p.symbol,
      weightPct: invested > 0 ? ((p.marketValue ?? 0) / invested) * 100 : 0,
      qty: p.qty,
      avgEntryPrice: p.avgEntryPrice,
      currentPrice: p.currentPrice,
      marketValue: p.marketValue,
      unrealizedPL: p.unrealizedPL,
      unrealizedPLPct:
        p.unrealizedPLPercentage !== undefined ? p.unrealizedPLPercentage / 100 : undefined,
      heldSince: p.heldSince,
      lastSoldAt: p.lastSoldAt,
    }));

    return { rows: mapped };
  }, [content]);

  const sortedRows = useMemo(() => {
    const sorted = [...rows];
    sorted.sort((a, b) => {
      const getVal = (row: PositionRow): number | string => {
        switch (sortKey) {
          case 'symbol':
            return row.symbol;
          case 'weight':
            return row.weightPct;
          case 'avgEntryPrice':
            return row.avgEntryPrice ?? 0;
          case 'currentPrice':
            return row.currentPrice ?? 0;
          case 'marketValue':
            return row.marketValue ?? 0;
          case 'unrealizedPL':
            return row.unrealizedPL ?? 0;
          case 'unrealizedPLPct':
            return row.unrealizedPLPct ?? 0;
          default:
            return 0;
        }
      };
      const av = getVal(a);
      const bv = getVal(b);
      if (typeof av === 'string' && typeof bv === 'string') {
        return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      return sortDir === 'asc' ? Number(av) - Number(bv) : Number(bv) - Number(av);
    });
    return sorted;
  }, [rows, sortKey, sortDir]);

  const handleRowKeyDown = (
    event: React.KeyboardEvent<HTMLTableRowElement>,
    row: PositionRow,
  ) => {
    if (!onPositionSelect) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onPositionSelect(row);
    }
  };

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir(key === 'symbol' ? 'asc' : 'desc');
    }
  };

  const sortIndicator = (key: SortKey) =>
    sortKey === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  const { investedTotal, totalUnrealizedPL, totalUnrealizedPLPct } = useMemo(
    () => computePositionTotals(content),
    [content],
  );
  const totalPlUp = isPlUp(totalUnrealizedPL, totalUnrealizedPLPct) ?? false;

  if (loading) {
    return <p className="m-0 text-sm text-muted-foreground">Loading positions…</p>;
  }
  if (error) {
    return (
      <div className="rounded-lg border border-[color:var(--nq-destructive-border)] bg-[color:var(--nq-destructive-soft)] px-4 py-3 text-sm text-[color:var(--nq-down-soft)]">
        {error}
      </div>
    );
  }
  if (isUnfunded || rows.length === 0) {
    return (
      <DataCard title="Positions" className="min-w-0">
        <p className="m-0 text-sm text-muted-foreground">
          {isUnfunded
            ? 'No positions yet — fund your account to start investing.'
            : 'No open positions.'}
        </p>
      </DataCard>
    );
  }

  const columns: [SortKey, string][] = [
    ['symbol', 'Symbol'],
    ['weight', 'Portfolio %'],
    ['avgEntryPrice', 'Avg entry'],
    ['currentPrice', 'Current'],
    ['marketValue', 'Market value'],
    ['unrealizedPL', 'Unrealized P/L'],
    ['unrealizedPLPct', 'Unrealized P/L %'],
  ];

  return (
    <DataCard title="Positions" className="min-w-0">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map(([key, label]) => (
                <TableHead
                  key={key}
                  className={headClassName(key, sortKey === key)}
                  onClick={() => handleSort(key)}
                >
                  {label}
                  {sortIndicator(key)}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow className="border-b-2 border-b-[color:rgba(255,255,255,0.12)] bg-[color:rgba(255,255,255,0.04)] font-semibold text-foreground hover:bg-[color:rgba(255,255,255,0.04)]">
              <TableCell className={cellClassName('symbol')}>
                Total · {rows.length} positions
              </TableCell>
              <TableCell className={cellClassName('weight')}>100%</TableCell>
              <TableCell colSpan={2} />
              <TableCell className={cellClassName('marketValue')}>
                {formatCurrency(investedTotal)}
              </TableCell>
              <TableCell
                className={cellClassName(
                  'unrealizedPL',
                  totalPlUp ? 'text-positive' : 'text-negative',
                )}
              >
                {totalPlUp ? '+' : ''}
                {formatCurrency(totalUnrealizedPL)}
              </TableCell>
              <TableCell className={cellClassName('unrealizedPLPct')}>
                {totalUnrealizedPLPct !== undefined ? (
                  <PLBadge value={totalPlUp ? 1 : -1}>
                    {formatPercent(totalUnrealizedPLPct)}
                  </PLBadge>
                ) : (
                  '—'
                )}
              </TableCell>
            </TableRow>
            {sortedRows.map((p) => {
              const plUp = isPlUp(p.unrealizedPL, p.unrealizedPLPct);
              const plDefined = plUp !== undefined;
              const isSelected = selectedSymbol === p.symbol;
              const rowTone = isSelected
                ? 'bg-primary/10 hover:bg-primary/14'
                : plDefined
                  ? plUp
                    ? 'bg-positive/6'
                    : 'bg-negative/6'
                  : '';
              const selectable = onPositionSelect
                ? 'cursor-pointer hover:bg-primary/6'
                : '';
              return (
                <TableRow
                  key={p.symbol}
                  className={`${rowTone} ${selectable}`.trim() || undefined}
                  onClick={onPositionSelect ? () => onPositionSelect(p) : undefined}
                  onKeyDown={onPositionSelect ? (e) => handleRowKeyDown(e, p) : undefined}
                  role={onPositionSelect ? 'button' : undefined}
                  tabIndex={onPositionSelect ? 0 : undefined}
                  title={onPositionSelect ? 'View price chart & details' : undefined}
                >
                  <TableCell className={cellClassName('symbol')}>
                    <span className="font-display font-bold">{p.symbol}</span>
                  </TableCell>
                  <TableCell className={cellClassName('weight')}>
                    {p.weightPct.toFixed(2)}%
                  </TableCell>
                  <TableCell className={cellClassName('avgEntryPrice')}>
                    {formatMaybeCurrency(p.avgEntryPrice)}
                  </TableCell>
                  <TableCell className={cellClassName('currentPrice')}>
                    {formatMaybeCurrency(p.currentPrice)}
                  </TableCell>
                  <TableCell className={cellClassName('marketValue')}>
                    {formatMaybeCurrency(p.marketValue)}
                  </TableCell>
                  <TableCell
                    className={cellClassName(
                      'unrealizedPL',
                      plDefined ? (plUp ? 'text-positive' : 'text-negative') : undefined,
                    )}
                  >
                    {p.unrealizedPL === undefined
                      ? '—'
                      : `${p.unrealizedPL >= 0 ? '+' : ''}${formatCurrency(p.unrealizedPL)}`}
                  </TableCell>
                  <TableCell className={cellClassName('unrealizedPLPct')}>
                    {p.unrealizedPLPct !== undefined ? (
                      <PLBadge value={plUp ? 1 : -1}>
                        {formatPercent(p.unrealizedPLPct)}
                      </PLBadge>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </DataCard>
  );
};

export default PortfolioPositionsTable;
